/**
 * MapKey — cartographic symbol legend anchored above the scale bar.
 *
 * Distinct from LegendControl (which is a layer manager). This shows
 * the SLD rules currently in effect — one swatch + label per rule,
 * grouped by layer. Renders only layers that are (a) visible and
 * (b) have rule summaries supplied by the SLD loader.
 *
 * API:
 *   const mk = new MapKey(map);
 *   mk.setLayer(layerDef, ruleSummaries);   // attach/refresh entry
 *   mk.setVisible(layerDef, true|false);    // toggle entry visibility
 *   mk.removeLayer(layerDef);               // detach (rare; for dispose)
 */
export default class MapKey {
  constructor(map) {
    this.map = map;
    this._entries = new Map();  // layerDef → { ruleSummaries, visible }

    this.containerEl = document.createElement('div');
    this.containerEl.id = 'map-key';
    this.containerEl.style.display = 'none';
    document.getElementById('map').appendChild(this.containerEl);
  }

  setLayer(layerDef, ruleSummaries, classificationAttributes) {
    const existing = this._entries.get(layerDef);
    this._entries.set(layerDef, {
      ruleSummaries: ruleSummaries,
      classificationAttributes: classificationAttributes || [],
      // Preserve visibility across re-attach (e.g. style hot-reloads).
      visible: existing ? existing.visible : layerDef.layer.getVisible()
    });
    this._render();
  }

  setVisible(layerDef, visible) {
    const entry = this._entries.get(layerDef);
    if (!entry) return;  // nothing to show until rule summaries arrive
    entry.visible = visible;
    this._render();
  }

  removeLayer(layerDef) {
    this._entries.delete(layerDef);
    this._render();
  }

  _render() {
    const sections = [];
    // Render in legend stacking order (topmost first) so the key
    // reads top-to-bottom matching what's painted on the map.
    for (const [layerDef, entry] of this._entries) {
      if (!entry.visible) continue;
      if (!entry.ruleSummaries || entry.ruleSummaries.length === 0) continue;
      sections.push(this._renderSection(
        layerDef, entry.ruleSummaries, entry.classificationAttributes
      ));
    }

    if (sections.length === 0) {
      this.containerEl.style.display = 'none';
      this.containerEl.innerHTML = '';
      return;
    }

    this.containerEl.style.display = '';
    this.containerEl.innerHTML = sections.join('');
  }

  _renderSection(layerDef, rules, classificationAttributes) {
    let html = '<div class="map-key-section">';
    // Title format: "{layer label} — {attr}" when the SLD's filters
    // reference one or more attributes; falls back to just the layer
    // label for filterless styles. Multiple attrs are comma-joined.
    const attrs = (classificationAttributes || []).join(', ');
    const title = attrs
      ? _esc(layerDef.label) + ' — <span class="map-key-attr">' +
        _esc(attrs) + '</span>'
      : _esc(layerDef.label);
    html += '<div class="map-key-title">' + title + '</div>';
    for (const rule of rules) {
      html += '<div class="map-key-row">' +
        _swatch(rule) +
        '<span class="map-key-label">' + _esc(rule.label) + '</span>' +
        '</div>';
    }
    html += '</div>';
    return html;
  }
}

function _swatch(rule) {
  const fill = rule.fill || 'transparent';
  const stroke = rule.stroke || '#666';
  const strokeWidth = Math.max(1, Math.min(3, Number(rule.strokeWidth) || 1));
  const fillOp = rule.fillOpacity != null ? Number(rule.fillOpacity) : 1;

  // Three swatch shapes match the three SLD vector symbolizer types.
  // Defaults to the polygon swatch for unknown / missing types since
  // that's the most informative (shows both fill and stroke).
  if (rule.symbolType === 'linesymbolizer') {
    // swatchOpacity is set by sld-edge-cases when a polygon symbolizer
    // is recast as a line (transparent fill, visible stroke) so the
    // swatch shows the stroke at the stroke's authored opacity.
    const swOp = rule.swatchOpacity != null ? Number(rule.swatchOpacity) : 1;
    return '<span class="map-key-swatch map-key-swatch-line" ' +
      'style="background:' + stroke + ';height:' + strokeWidth +
      'px;opacity:' + swOp + '"></span>';
  }
  if (rule.symbolType === 'pointsymbolizer') {
    return '<span class="map-key-swatch map-key-swatch-point" ' +
      'style="background:' + fill + ';opacity:' + fillOp +
      ';border-color:' + stroke +
      ';border-width:' + strokeWidth + 'px"></span>';
  }
  // polygonsymbolizer (and fallback)
  return '<span class="map-key-swatch map-key-swatch-polygon" ' +
    'style="background:' + fill + ';opacity:' + fillOp +
    ';border-color:' + stroke +
    ';border-width:' + strokeWidth + 'px"></span>';
}

function _esc(s) {
  const d = document.createElement('div');
  d.textContent = s == null ? '' : String(s);
  return d.innerHTML;
}
